import _ from 'lodash';
import {
  lsApiService,
  lsErrorService,
  lsStorageService,
  lsStorageServiceConstants,
  lsTrackingService,
  lsToastService,
} from 'ls-vue-common';
import {
  SHARE__RESET,
  SHARE__MUTATE_LOADING_STATUS,
  SHARE__TRACK_COMPONENT_VIEWED,
  SHARE__SHARE_VIA_PLATFORM,
  SHARE__SHARE_VIA_EMAIL,
  SHARE__CUSTOMER_REFERRAL,
  SHARE__PLATFORMS,
  SHARE__PRIMARY_PLATFORM,
  SHARE__WIDGET_PLATFORM,
} from '@/store/types';
import { lsShareStoreHelpers } from './share.helpers';
import { SharePlatformDisplayConstants, ShareVariationConstants } from './share.constants';
import { lsModuleHelpers } from '../helpers';

const createInitialState = () => {
  return {
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [SHARE__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [SHARE__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
};

export const actions = {
  [SHARE__TRACK_COMPONENT_VIEWED]({ getters }, { componentName }) {
    const { customer_referraltest_id, test_id, test_tag, variation } =
      getters[SHARE__CUSTOMER_REFERRAL] || {};

    lsTrackingService.track({
      event: 'customer_share_variation_component_shown',
      data: {
        ui_component_rendered: componentName,
        customer_referraltest_id,
        test_id,
        test_tag,
        ..._.reduce(
          variation,
          (result, variationName, variationType) => {
            _.set(result, `variation_${variationType}`, variationName);
            return result;
          },
          {}
        ),
      },
    });
  },

  [SHARE__SHARE_VIA_PLATFORM]({ rootState }, { platform, property_id = null }) {
    const { platformName } = platform;
    lsStorageService.set(
      _.get(lsStorageServiceConstants, `SHARE_${_.toUpper(platformName)}`),
      true
    );

    if (_.isNull(property_id)) {
      property_id = _.head(_.keys(_.get(rootState, 'properties.propertiesById')));
    }

    if (platformName !== 'link' && platformName !== 'email') {
      const sites = _.get(rootState, `properties.propertiesById[${property_id}].sites`, {});

      const url = lsShareStoreHelpers.getURL({ platform, sites });

      window.open(url);

      lsTrackingService.track({
        event: 'open-external',
        target: `${platformName}-window`,
      });
    }
  },

  [SHARE__SHARE_VIA_EMAIL]({ rootState, getters, commit, dispatch }, { emails, onSuccess }) {
    commit(SHARE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    // ensure pending flag is set locally
    dispatch(SHARE__SHARE_VIA_PLATFORM, { platform: { platformName: 'email' } });

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/refer`,
        payload: {
          emails: _.map(_.uniq(_.map(_.compact(emails), _.toLower)), (email) => {
            return { email };
          }),
          deal: _.get(getters[SHARE__CUSTOMER_REFERRAL], 'variation_values.deal.deal_id'),
        },
      })
      .then(() => {
        onSuccess();
        lsToastService.open({
          message: 'Your invites have been sent!',
          type: 'is-success',
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SHARE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
};

export const getters = {
  [SHARE__CUSTOMER_REFERRAL](state, getters, rootState) {
    return _.get(rootState, 'auth.user.customer.referral', null);
  },
  [SHARE__PLATFORMS](state, getters) {
    return _.reduce(
      _.get(getters[SHARE__CUSTOMER_REFERRAL], 'variation_ui_components.platforms'),
      (result, platform, platformName) => {
        const iconsAndColor = SharePlatformDisplayConstants[platformName];

        result[platformName] = lsShareStoreHelpers.generatePlatformWithUIData({
          platform,
          iconsAndColor,
        });

        return result;
      },
      {}
    );
  },
  [SHARE__PRIMARY_PLATFORM](state, getters) {
    const variationName = _.get(getters[SHARE__CUSTOMER_REFERRAL], 'variation.web', 'default');

    const { platformName } = ShareVariationConstants.web[variationName];
    const platform = getters[SHARE__PLATFORMS][platformName];

    if (!platform) {
      return null;
    }

    return {
      ...platform,
    };
  },

  [SHARE__WIDGET_PLATFORM](state, getters) {
    const variationName = _.get(
      getters[SHARE__CUSTOMER_REFERRAL],
      'variation.web_services',
      'default'
    );

    const { platformName } = ShareVariationConstants.web_services[variationName];
    const platform = getters[SHARE__PLATFORMS][platformName];

    if (!platform) {
      return null;
    }

    return {
      ...platform,
    };
  },
};

export const share = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
